import * as React from 'react';

import Bee from '@beefree.io/sdk';
import ResizeObserver from 'resize-observer-polyfill';

import {
    EDITOR_FONTS_CONFIGURATION,
    NUTSHELL_FONTS_CONFIGURATION,
} from './editor-fonts-configuration';
import {emptyTemplate} from './empty-template';
import {
    beeJsonStringReplaceUnsubscribeToken,
    beeJsonStringReplaceDetailsToken,
    beeJsonStringReplaceUnsubscribeUrl,
    beeJsonStringReplaceDetailsUrl,
} from './json-helpers';
import {AddMergeField} from './merge-tags/add-merge-field';
import type {BeeRejectFunction, BeeResolveFunction} from './merge-tags/add-merge-field';
import {SizeWarning} from './size-warning';

import styles from './bee-editor.module.css';

// width where the
const MIN_WIDTH = 1025;

type Props = {
    beeToken: string;
    onSendEmail?: (toEmailAddresses: string[], html: string) => Promise<any>;
    marketingEmailTemplate?: string | null | undefined;
    onSave?: (beeContentJson: string, beeContentHtml: string) => Promise<any>;
    onChange: (beeContentJson: string, editionId: string) => any;
    getBeeRef?: (bee: Bee) => void;
    isPreview?: boolean;
    testEmailToAddress?: string[];
    editionId: string;
    editionPreviewUrl: string;
    shouldReload?: boolean;
    setShouldReload?: (arg1: boolean) => void;
};

type State = {
    mergeTagHandlers?: {
        resolve: BeeResolveFunction;
        reject: BeeRejectFunction;
    };
    showSizeWarning: boolean;
};

export class BeeEditor extends React.Component<Props, State> {
    config: any;
    beeTest: Bee;
    containerDiv: HTMLElement | null | undefined;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    previousContainerWidth: number;

    constructor(props: Props) {
        super(props);

        this.state = {
            mergeTagHandlers: undefined,
            showSizeWarning: false,
        };

        this.previousContainerWidth = 0;

        this.beeTest = new Bee(JSON.parse(props.beeToken));
        if (props.getBeeRef) {
            props.getBeeRef(this.beeTest);
        }

        const beeUid = NutshellFrontendSettings.instanceId
            ? `instance_${NutshellFrontendSettings.instanceId}`
            : null;

        const editorFonts = NutClientConfig.featureFlagNutshellOnly
            ? NUTSHELL_FONTS_CONFIGURATION
            : EDITOR_FONTS_CONFIGURATION;

        this.config = {
            uid: beeUid,
            container: 'bee-plugin-container',
            customCss: `${location.origin}/include/bee-editor/bee-editor-styles.module.css`,
            editorFonts: editorFonts,
            trackChanges: true, // Needed for onChange function to always work
            contentDialog: {
                // Our merge tags might need a fallback, so show our own dialog
                // where the user can select a merge tag and add an optional
                // fallback value
                mergeTags: {
                    label: 'Merge tags',
                    // @ts-expect-error
                    handler: (resolve, reject) => {
                        this.setState({mergeTagHandlers: {resolve, reject}});
                    },
                },
            },
            specialLinks: [
                {
                    type: 'Required links',
                    label: 'Unsubscribe / manage preferences',
                    link: `${location.origin}/unsubscribe/${props.editionId}`,
                },
                {
                    type: 'Other links',
                    label: 'View in browser link',
                    link: props.editionPreviewUrl,
                },
            ],
            contentDefaults: {
                text: {
                    html: 'I’m a new Text block ready for your content.',
                    styles: {
                        color: '#555555',
                        linkColor: '#007089',
                        fontSize: '16px',
                        lineHeight: '150%', // Defaults to the second of four options in the BEE Editor
                        fontFamily: 'Arial, Helvetica, sans-serif',
                    },
                    blockOptions: {
                        paddingBottom: '10px',
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        paddingTop: '10px',
                    },
                },
            },
            mergeContents: [],
            onChange: (beeContentJson: string) => {
                try {
                    return props.onChange(
                        beeJsonStringReplaceDetailsUrl(
                            beeJsonStringReplaceUnsubscribeUrl(beeContentJson),
                            this.props.editionPreviewUrl
                        ),
                        this.props.editionId
                    );
                } catch {
                    // @ts-expect-error
                    window.trackJs.track('Bee content not saved, onChange failed');
                }
            },
            onSave: (beeContentJson: string, beeContentHtml: string) => {
                const onSave = props.onSave;
                if (onSave) {
                    return onSave(
                        beeJsonStringReplaceDetailsUrl(
                            beeJsonStringReplaceUnsubscribeUrl(beeContentJson),
                            this.props.editionPreviewUrl
                        ),
                        beeJsonStringReplaceDetailsUrl(
                            beeJsonStringReplaceUnsubscribeUrl(beeContentHtml),
                            this.props.editionPreviewUrl
                        )
                    );
                }
            },
            onSend: props.onSendEmail
                ? // @ts-expect-error
                  (htmlFile) => {
                      const {onSendEmail} = props;

                      const emailAddress = this.props.testEmailToAddress;

                      if (onSendEmail && emailAddress) {
                          onSendEmail(emailAddress, htmlFile)
                              .then(() => {})
                              .catch(() => {});
                      }
                  }
                : undefined,
            onLoad: () => {
                if (this.props.isPreview) {
                    this.beeTest.preview();
                }
            },
            // @ts-expect-error
            onError: (errorMessage) => {
                // eslint-disable-next-line no-console
                console.log('onError: ', errorMessage);
            },
            // @ts-expect-error
            onWarning: (alertMessage) => {
                // eslint-disable-next-line no-console
                console.log('onWarning: ', alertMessage);
            },
        };
    }

    componentDidMount() {
        // Show warning if below width threshold and editor doesn't show properly
        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.containerDiv) {
                    const containerWidth = entry.contentRect.width;

                    this.setState({
                        showSizeWarning: !this.props.isPreview && containerWidth < MIN_WIDTH,
                    });

                    // If the container was hidden (width 0) and is now visible,
                    // and we're in preview mode, reload the preview so it sizes
                    // correctly
                    if (
                        this.previousContainerWidth === 0 &&
                        containerWidth > 0 &&
                        this.props.isPreview &&
                        this.beeTest &&
                        this.beeTest.instance
                    ) {
                        // Use requestAnimationFrame to ensure layout is complete
                        requestAnimationFrame(() => {
                            if (this.beeTest && this.beeTest.instance) {
                                // Exit and re-enter preview mode to force a recalculation
                                this.beeTest.instance.togglePreview();
                                requestAnimationFrame(() => {
                                    if (this.beeTest && this.beeTest.preview) {
                                        this.beeTest.preview();
                                    }
                                });
                            }
                        });
                    }

                    this.previousContainerWidth = containerWidth;
                }
            }
        });

        if (this.containerDiv && this.containerDiv instanceof HTMLElement) {
            this.resizeObserver.observe(this.containerDiv);
        }

        if (this.props.marketingEmailTemplate) {
            this.beeTest.start(
                this.config,
                JSON.parse(
                    beeJsonStringReplaceDetailsToken(
                        beeJsonStringReplaceUnsubscribeToken(
                            this.props.marketingEmailTemplate,
                            this.props.editionId
                        ),
                        this.props.editionPreviewUrl
                    )
                )
            );
        } else {
            this.beeTest.start(this.config, emptyTemplate(this.props.editionId));
        }

        // Override default onSave
        // @ts-expect-error
        this.beeTest.onSave = () => {};
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            this.beeTest &&
            this.beeTest.instance &&
            nextProps.isPreview !== this.props.isPreview &&
            nextProps.editionId === this.props.editionId
        ) {
            // This logic only applies when the tabs are switched between for the same edition
            if (nextProps.isPreview) {
                // If we want preview mode, we can just go immediately into it
                this.beeTest.preview();
            } else {
                // If we want to exit preview mode, the only way to do that is
                // to toggle preview. Because this could be prone to bugs if we
                // get out of sync, we call preview explicitly above. Also, the
                // `preview` function is on the BEE plugin instance, but the
                // `togglePreview` function is only on the BEE instance itself.
                this.beeTest.instance.togglePreview();
            }
        }

        // Reload marketingEmailTemplate to editor if the edition changes
        if (
            this.beeTest &&
            this.beeTest.instance &&
            nextProps.marketingEmailTemplate &&
            nextProps.editionId !== this.props.editionId
        ) {
            this.beeTest.instance.load(
                JSON.parse(
                    beeJsonStringReplaceDetailsToken(
                        beeJsonStringReplaceUnsubscribeToken(
                            nextProps.marketingEmailTemplate,
                            nextProps.editionId
                        ),
                        nextProps.editionPreviewUrl
                    )
                )
            );

            // Set to preview mode if needed - unfortunately this does not work when done in the
            // onLoad callback, because this.props.isPreview may be incorrect
            if (nextProps.isPreview) {
                this.beeTest.preview();
            }
        }

        // Reload the marketingEmailTemplate to the editor if changes have been reverted
        if (
            this.beeTest &&
            this.beeTest.instance &&
            this.props.marketingEmailTemplate &&
            this.props.setShouldReload &&
            nextProps.shouldReload
        ) {
            this.beeTest.instance.load(
                JSON.parse(
                    beeJsonStringReplaceDetailsToken(
                        beeJsonStringReplaceUnsubscribeToken(
                            this.props.marketingEmailTemplate,
                            this.props.editionId
                        ),
                        this.props.editionPreviewUrl
                    )
                )
            );

            const setShouldReload = this.props.setShouldReload;
            if (setShouldReload) {
                // Update that reload has been completed
                setShouldReload(false);
            }
        }
    }

    render() {
        const {mergeTagHandlers, showSizeWarning} = this.state;

        return (
            <>
                <div
                    ref={(node) => {
                        this.containerDiv = node;
                    }}
                    className={styles.container}
                >
                    <div id='bee-plugin-container' className={styles['bee-wrapper']}></div>
                    {showSizeWarning ? <SizeWarning /> : undefined}
                </div>
                {mergeTagHandlers ? (
                    <AddMergeField
                        onClose={() => this.setState({mergeTagHandlers: undefined})}
                        reject={() => {
                            this.setState({mergeTagHandlers: undefined});
                            mergeTagHandlers.reject();
                        }}
                        resolve={(params) => {
                            this.setState({mergeTagHandlers: undefined});
                            mergeTagHandlers.resolve(params);
                        }}
                    />
                ) : undefined}
            </>
        );
    }
}
